import { Link } from "@tanstack/react-router";

import { useState } from "react";

import { useCustomer360, useSetCustomerGhost, type Customer360, type NextBestAction } from "../queries/useCustomer360";
import { GhostMark, UNGHOST_REMINDER } from "./GhostMark";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Customer360SmsTab } from "./Customer360SmsTab";
import { Timeline } from "./Timeline";

function CustomerGhostPanel({
  contactId,
  ghost,
}: {
  contactId: number;
  ghost?: Customer360["ghost"];
}) {
  const ghosted = Boolean(ghost?.ghosted);
  const update = useSetCustomerGhost(contactId);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  return (
    <div className={ghosted ? "rounded-lg border border-slate-300 bg-slate-50 p-4" : "rounded-lg border border-slate-200 p-4"}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Ghost mode</div>
      <p className="mt-1 text-sm text-slate-600">
        {ghosted
          ? "Outbound SMS, email, reminders, and review requests are stopped. The customer is not told."
          : "Silently stop every outbound message to this client. The quote status stays unchanged."}
      </p>
      {ghosted && ghost?.ghost_reason ? (
        <p className="mt-2 text-sm text-slate-800">Internal reason: {ghost.ghost_reason}</p>
      ) : null}
      {!ghosted ? (
        <textarea
          className="mt-3 min-h-[64px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Internal reason. Never sent to the client."
        />
      ) : null}
      {ghosted ? <p className="mt-2 text-sm text-slate-600">{UNGHOST_REMINDER}</p> : null}
      {error ? <p className="mt-2 text-sm text-red-700">{error}</p> : null}
      <Button
        className="mt-3"
        variant={ghosted ? "secondary" : "default"}
        disabled={update.isPending || (!ghosted && reason.trim().length < 3)}
        onClick={() => {
          setError("");
          update.mutate(
            ghosted ? { ghosted: false } : { ghosted: true, reason: reason.trim() },
            { onError: (err) => setError(err instanceof Error ? err.message : "Ghost update failed") },
          );
        }}
      >
        {ghosted ? "Remove ghost mode" : "Ghost this client"}
      </Button>
    </div>
  );
}

function formatUsd(value?: number) {
  return `$${Math.round(value || 0).toLocaleString()}`;
}

function formatDate(value?: string) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function totalLtv(data: Customer360) {
  return data.quotes.reduce((sum, quote) => sum + (Number(quote.total_usd) || 0), 0);
}

export function Customer360View({ contactId }: { contactId: number }) {
  const { data, isLoading, isError } = useCustomer360(contactId);
  const [activeTab, setActiveTab] = useState<"overview" | "sms">("overview");

  if (isLoading) return <Card className="p-6 text-sm text-slate-500">Loading Customer 360...</Card>;
  if (isError || !data) {
    return (
      <Card className="p-6">
        <div className="text-sm font-medium text-slate-950">Customer 360 unavailable</div>
        <p className="mt-1 text-sm text-slate-500">Check the backend endpoint, admin token, or contact id.</p>
      </Card>
    );
  }

  const returning = data.quotes.length >= 2;

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="slate">Customer 360</Badge>
            {returning ? <Badge variant="green">Returning</Badge> : null}
          </div>
          <h1 className="mt-3 text-2xl font-semibold text-slate-950">
            {data.customer.full_name}
            <GhostMark ghosted={data.ghost?.ghosted} />
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {data.customer.email} · {data.customer.phone || "No phone"}
          </p>
        </div>
        <NextBestActionCta action={data.next_best_action} />
      </div>

      <CustomerGhostPanel contactId={data.customer.id} ghost={data.ghost} />

      {data.compliance_flags.has_opt_out ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Compliance flag: this customer has {data.compliance_flags.opt_outs.length} opt-out record(s). Review before any outbound touch.
        </div>
      ) : (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          Compliance clean: no opt-out flags found for this customer.
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <InfoCard label="LTV" value={formatUsd(totalLtv(data))} />
        <InfoCard label="Quotes" value={String(data.quotes.length)} />
        <InfoCard label="First seen" value={formatDate(data.customer.first_seen_at)} />
        <InfoCard label="Last activity" value={formatDate(data.customer.last_seen_at)} />
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
        <Button
          variant={activeTab === "overview" ? "secondary" : "ghost"}
          className={activeTab === "overview" ? "bg-white shadow-sm" : ""}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </Button>
        <Button
          variant={activeTab === "sms" ? "secondary" : "ghost"}
          className={activeTab === "sms" ? "bg-white shadow-sm" : ""}
          onClick={() => setActiveTab("sms")}
        >
          SMS
        </Button>
      </div>

      {activeTab === "overview" ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Quote History</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="text-left text-xs uppercase tracking-[0.12em] text-slate-500">
                  <tr>
                    <th className="py-2">Quote</th>
                    <th className="py-2">Amount</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">Created</th>
                    <th className="py-2 text-right">Open</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.quotes.map((quote) => (
                    <tr key={quote.quote_id}>
                      <td className="py-3 font-medium text-slate-950">#{quote.quote_id}</td>
                      <td className="py-3 text-slate-700">{formatUsd(quote.total_usd)}</td>
                      <td className="py-3"><Badge variant="slate">{quote.status}</Badge></td>
                      <td className="py-3 text-slate-500">{formatDate(quote.created_on)}</td>
                      <td className="py-3 text-right">
                        <Link className="text-sm font-medium text-blue-700 hover:text-blue-900" to="/quotes/$quoteId" params={{ quoteId: quote.quote_id }}>
                          Quote 360
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline events={data.timeline} />
            </CardContent>
          </Card>
        </>
      ) : (
        <Customer360SmsTab contactId={contactId} active={activeTab === "sms"} />
      )}
    </section>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-2 text-lg font-semibold text-slate-950">{value}</div>
    </Card>
  );
}

function NextBestActionCta({ action }: { action: NextBestAction }) {
  const critical = action.priority === "critical";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm md:min-w-[280px]">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Next Best Action</div>
      <div className="mt-1 text-sm font-semibold text-slate-950">{action.action.replace(/_/g, " ")}</div>
      <p className="mt-1 text-xs text-slate-500">{action.reason}</p>
      <Button className="mt-3 w-full" variant={critical ? "default" : "secondary"}>
        {critical ? "Call Now" : "Inspect Timeline"}
      </Button>
    </div>
  );
}
